import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download, Filter, ScrollText, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { http } from '../api/client';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DataTable, Pagination, type Column } from '../components/ui/DataTable';
import { Select } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { dateTime } from '../lib/format';
import { useUi } from '../store/ui';
import type { AuditRow, Page } from '../types';

type SortKey = 'created_at' | 'action' | 'actor_name';

interface Facets {
  actions: Array<{ action: string; n: string }>;
  entities: Array<{ entity: string; n: string }>;
  actors: Array<{ id: string; name: string }>;
  statuses: Array<{ status: string; n: string }>;
}

/**
 * Read-only activity log. There is deliberately no delete or edit path:
 * an audit trail you can rewrite is not an audit trail.
 */
export function AuditPage() {
  const toast = useUi((s) => s.toast);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [action, setAction] = useState('');
  const [actorId, setActorId] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'created_at', dir: 'desc' });
  const [detail, setDetail] = useState<AuditRow | null>(null);

  const { data: facets } = useQuery({ queryKey: ['audit', 'facets'], queryFn: () => http.get<Facets>('/audit/facets') });
  const { data, isLoading } = useQuery({
    queryKey: ['audit', page, search, status, action, actorId, sort],
    queryFn: () =>
      http.get<Page<AuditRow>>('/audit', {
        page, limit: 20, q: search, status, action, actorId, sort: sort.key, dir: sort.dir,
      }),
    placeholderData: keepPreviousData,
  });

  /** Client-side CSV export of the current page. Phase 6 adds server-side export. */
  const exportCsv = () => {
    const rows = data?.items ?? [];
    if (!rows.length) { toast({ kind: 'info', title: 'Nothing to export' }); return; }
    const header = ['created_at', 'action', 'status', 'actor_name', 'actor_role', 'entity', 'entity_id', 'description', 'ip_address'];
    const csv = [
      header.join(','),
      ...rows.map((r) =>
        header
          .map((h) => JSON.stringify(String((r as unknown as Record<string, unknown>)[h] ?? '')))
          .join(','),
      ),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ kind: 'success', title: 'CSV exported', message: `${rows.length} rows from this page.` });
  };

  const columns: Column<AuditRow>[] = useMemo(() => [
    {
      key: 'created_at', header: 'When', sortable: true, className: 'whitespace-nowrap',
      render: (r) => <span className="text-xs text-ink-muted">{dateTime(r.created_at)}</span>,
    },
    {
      key: 'action', header: 'Action', sortable: true,
      render: (r) => (
        <span className="min-w-0">
          <span className="block truncate font-mono text-xs text-ink">{r.action}</span>
          {r.description && <span className="block truncate text-xs text-ink-faint">{r.description}</span>}
        </span>
      ),
    },
    {
      key: 'actor', header: 'Who',
      render: (r) => (
        <span className="block text-xs">
          <span className="font-medium text-ink">{r.actor_name ?? 'system'}</span>
          {r.actor_role && <span className="block text-ink-faint">{r.actor_role}</span>}
        </span>
      ),
    },
    { key: 'status', header: 'Result', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'entity', header: 'Target', className: 'hidden lg:table-cell',
      render: (r) => (
        <span className="text-xs text-ink-muted">
          {r.entity ?? '—'}
          {r.entity_id && <span className="block truncate font-mono text-[11px] text-ink-faint">{r.entity_id.slice(0, 8)}…</span>}
        </span>
      ),
    },
    {
      key: 'ip', header: 'IP', className: 'hidden xl:table-cell',
      render: (r) => <span className="font-mono text-[11px] text-ink-faint">{r.ip_address ?? '—'}</span>,
    },
  ], []);

  const activeFilters = [status, action, actorId, search].filter(Boolean).length;

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-ink">
            <ScrollText className="h-5 w-5 text-brand-600" /> Audit log
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Append-only record of who did what, when, and from where. Denied attempts are logged too.
          </p>
        </div>
        <Button variant="secondary" onClick={exportCsv}><Download className="h-4 w-4" /> Export CSV</Button>
      </header>

      <Card>
        <div className="grid gap-2 border-b border-line p-3 lg:grid-cols-[1fr_repeat(3,minmax(0,180px))]">
          <div className="relative">
            <Search className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-faint" aria-hidden />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search description, actor or action…"
              aria-label="Search audit log"
              className="input-base ps-9"
            />
          </div>
          <Select aria-label="Filter by result" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All results</option>
            {facets?.statuses.map((s) => (
              <option key={s.status} value={s.status}>{s.status} ({s.n})</option>
            ))}
          </Select>
          <Select aria-label="Filter by action" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
            <option value="">All actions</option>
            {facets?.actions.map((a) => (
              <option key={a.action} value={a.action}>{a.action} ({a.n})</option>
            ))}
          </Select>
          <Select aria-label="Filter by person" value={actorId} onChange={(e) => { setActorId(e.target.value); setPage(1); }}>
            <option value="">Everyone</option>
            {facets?.actors.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </Select>
        </div>

        {activeFilters > 0 && (
          <div className="flex items-center gap-2 border-b border-line bg-surface-0 px-3 py-2">
            <Filter className="h-3.5 w-3.5 text-ink-faint" aria-hidden />
            <Badge tone="brand">{activeFilters} filter{activeFilters === 1 ? '' : 's'} active</Badge>
            <button
              onClick={() => { setSearch(''); setStatus(''); setAction(''); setActorId(''); setPage(1); }}
              className="ms-auto text-xs font-medium text-brand-600 hover:underline"
            >
              Clear all
            </button>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          loading={isLoading}
          sort={sort}
          onRowClick={setDetail}
          onSort={(key) =>
            setSort((s) => ({ key: key as SortKey, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }))
          }
          empty={
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-2 text-ink-faint">
                <ScrollText className="h-6 w-6" />
              </span>
              <p className="text-sm font-semibold text-ink">No entries match those filters</p>
            </div>
          }
          footer={data ? <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} /> : undefined}
        />
      </Card>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Audit entry" size="lg">
        {detail && (
          <dl className="space-y-3 text-sm">
            {[
              ['Action', detail.action],
              ['Result', detail.status],
              ['Actor', `${detail.actor_name ?? 'system'}${detail.actor_role ? ` (${detail.actor_role})` : ''}`],
              ['Description', detail.description ?? '—'],
              ['Entity', `${detail.entity ?? '—'}${detail.entity_id ? ` · ${detail.entity_id}` : ''}`],
              ['Branch', detail.branch_name ?? 'global'],
              ['IP address', detail.ip_address ?? '—'],
              ['Timestamp', new Date(detail.created_at).toISOString()],
            ].map(([label, value]) => (
              <div key={label} className="grid gap-1 sm:grid-cols-[140px_1fr]">
                <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">{label}</dt>
                <dd className="break-words text-ink">{value}</dd>
              </div>
            ))}
            <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
              <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">Payload</dt>
              <dd>
                <pre className="max-h-56 overflow-auto rounded-lg bg-surface-0 p-3 font-mono text-xs text-ink-muted">
                  {JSON.stringify(detail.meta ?? {}, null, 2)}
                </pre>
              </dd>
            </div>
          </dl>
        )}
      </Modal>
    </div>
  );
}
